<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import draggable from 'vuedraggable';
import CourseConfigApi from '../../sdk/CourseConfigApi';
import CommunityCourseItem from '../../sdk/models/CommunityCourseItem';
import AzureFunctionCall from '../../sdk/AzureFunctionsCall';

const router = useRouter();
const api = new CourseConfigApi();
const functionCall = new AzureFunctionCall();

// State
const loading = ref(true);
const saving = ref(false);
const isDirty = ref(false);
const statusMessage = ref('');
const statusType = ref<'success'|'error'|''>('');

interface CourseGroup {
  name: string;
  items: CommunityCourseItem[];
  collapsed: boolean;
  editing: boolean;
  editName: string;
}

const groups = ref<CourseGroup[]>([]);

// Available GroupRights (same as GroupForm.vue)
const availableGroupRights = [
  'HappyHomeschoolingWoche1',
  'HappyHomeschoolingWoche2',
  'HappyHomeschoolingWoche3',
  'HappyHomeschoolingWoche4',
  'HappyHomeschoolingWoche5',
  'HappyHomeschoolingWoche6',
  'HappyHomeschoolingQA',
  'HappyHomeschoolingForum',
  'HappyHomeschoolingClub',
];

// Editing overlay
const editingItem = ref<CommunityCourseItem | null>(null);
const editingGroupIndex = ref<number>(-1);
const showEditOverlay = ref(false);

// Upload state
const uploading = ref(false);
const uploadProgress = ref(0);
const uploadError = ref('');
const fileInputRef = ref<HTMLInputElement | null>(null);

function triggerFileUpload() {
  fileInputRef.value?.click();
}

async function handleFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !editingItem.value) return;

  // Reset file input so same file can be re-selected
  input.value = '';

  uploading.value = true;
  uploadProgress.value = 0;
  uploadError.value = '';

  try {
    // Determine MIME type
    const contentType = file.type || 'application/octet-stream';

    // Request SAS URL from backend
    const sasResponse = await functionCall.Post('generateuploadurl', {
      fileName: file.name,
      contentType: contentType,
    });
    const sasData = await sasResponse.json();

    // Upload directly to blob storage via XHR for progress tracking
    await uploadWithProgress(sasData.sasUrl, file, contentType);

    // Set the ContentUrl to the public blob URL
    editingItem.value.ContentUrl = sasData.blobUrl;
  } catch (e: any) {
    uploadError.value = 'Upload fehlgeschlagen: ' + e.message;
  } finally {
    uploading.value = false;
  }
}

function uploadWithProgress(sasUrl: string, file: File, contentType: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sasUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('x-ms-blob-content-type', contentType);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        uploadProgress.value = Math.round((event.loaded / event.total) * 100);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        uploadProgress.value = 100;
        resolve();
      } else {
        reject(new Error(`HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Netzwerkfehler'));
    xhr.onabort = () => reject(new Error('Upload abgebrochen'));

    xhr.send(file);
  });
}

// Load data
async function loadData() {
  loading.value = true;
  try {
    const items = await api.load();
    groupItems(items);
    isDirty.value = false;
  } catch (e: any) {
    showStatus('Fehler beim Laden: ' + e.message, 'error');
  } finally {
    loading.value = false;
  }
}

function groupItems(items: CommunityCourseItem[]) {
  const map = new Map<string, CommunityCourseItem[]>();
  // Preserve order from the flat array
  items.forEach(item => {
    const key = item.PartitionKey || 'Unbenannt';
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(item);
  });

  groups.value = Array.from(map.entries()).map(([name, items]) => ({
    name,
    items,
    collapsed: false,
    editing: false,
    editName: name,
  }));
}

// Flatten groups back to array
function flattenGroups(): CommunityCourseItem[] {
  const result: CommunityCourseItem[] = [];
  groups.value.forEach(group => {
    group.items.forEach(item => {
      item.PartitionKey = group.name;
      result.push(item);
    });
  });
  return result;
}

// Save
async function saveAll() {
  saving.value = true;
  statusMessage.value = '';
  try {
    const items = flattenGroups();
    await api.save(items);
    isDirty.value = false;
    showStatus('Erfolgreich gespeichert!', 'success');
  } catch (e: any) {
    showStatus('Fehler beim Speichern: ' + e.message, 'error');
  } finally {
    saving.value = false;
  }
}

function showStatus(msg: string, type: 'success'|'error') {
  statusMessage.value = msg;
  statusType.value = type;
  setTimeout(() => { statusMessage.value = ''; statusType.value = ''; }, 4000);
}

function markDirty() {
  isDirty.value = true;
}

// Group management
function addGroup() {
  const name = prompt('Name der neuen Gruppe:');
  if (name && name.trim()) {
    groups.value.push({
      name: name.trim(),
      items: [],
      collapsed: false,
      editing: false,
      editName: name.trim(),
    });
    markDirty();
  }
}

function startRenameGroup(group: CourseGroup) {
  group.editName = group.name;
  group.editing = true;
}

function confirmRenameGroup(group: CourseGroup) {
  if (group.editName.trim()) {
    group.name = group.editName.trim();
    group.editing = false;
    markDirty();
  }
}

function cancelRenameGroup(group: CourseGroup) {
  group.editName = group.name;
  group.editing = false;
}

function deleteGroup(group: CourseGroup, index: number) {
  if (group.items.length > 0) {
    if (!confirm(`Gruppe "${group.name}" enthält ${group.items.length} Einträge. Alle löschen?`)) {
      return;
    }
  }
  groups.value.splice(index, 1);
  markDirty();
}

// Item management
function addItem(group: CourseGroup) {
  const newItem = {
    PartitionKey: group.name,
    RowKey: null,
    ETag: null,
    Timestamp: null,
    Title: '',
    ContentType: 'mp4',
    ContentUrl: '',
    GroupRights: [],
  } as unknown as CommunityCourseItem;

  editingItem.value = { ...newItem };
  editingGroupIndex.value = groups.value.indexOf(group);
  showEditOverlay.value = true;
}

function editItem(item: CommunityCourseItem, groupIndex: number) {
  editingItem.value = {
    ...item,
    GroupRights: item.GroupRights ? [...item.GroupRights] : [],
  };
  editingGroupIndex.value = groupIndex;
  showEditOverlay.value = true;
}

function saveEditItem() {
  if (!editingItem.value) return;
  const group = groups.value[editingGroupIndex.value];
  if (!group) return;

  // Find the original item by reference (we stored groupIndex)
  // Check if this is a new item or an edit of an existing one
  let found = false;
  for (let i = 0; i < group.items.length; i++) {
    const orig = group.items[i];
    // Match by object identity won't work because we cloned. Use a temp marker.
    if ((orig as any).__editing) {
      group.items[i] = { ...editingItem.value };
      delete (group.items[i] as any).__editing;
      found = true;
      break;
    }
  }

  if (!found) {
    // New item
    group.items.push({ ...editingItem.value });
  }

  showEditOverlay.value = false;
  editingItem.value = null;
  markDirty();
}

function openEditItem(item: CommunityCourseItem, groupIndex: number) {
  (item as any).__editing = true;
  editingItem.value = {
    ...item,
    GroupRights: item.GroupRights ? [...item.GroupRights] : [],
  };
  editingGroupIndex.value = groupIndex;
  showEditOverlay.value = true;
}

function cancelEdit() {
  // Remove __editing marker from all items
  groups.value.forEach(g => g.items.forEach(i => delete (i as any).__editing));
  showEditOverlay.value = false;
  editingItem.value = null;
}

function deleteItem(group: CourseGroup, itemIndex: number) {
  if (confirm(`"${group.items[itemIndex].Title || 'Unbenannt'}" wirklich löschen?`)) {
    group.items.splice(itemIndex, 1);
    markDirty();
  }
}

function toggleGroupRight(right: string) {
  if (!editingItem.value) return;
  const idx = editingItem.value.GroupRights.indexOf(right);
  if (idx >= 0) {
    editingItem.value.GroupRights.splice(idx, 1);
  } else {
    editingItem.value.GroupRights.push(right);
  }
}

function onGroupDragChange() {
  markDirty();
}

function onItemDragChange() {
  markDirty();
}

function getContentTypeIcon(type: string) {
  if (type === 'mp4') return '🎬';
  if (type === 'pdf') return '📄';
  return '📎';
}

function goBack() {
  if (isDirty.value && !confirm('Es gibt ungespeicherte Änderungen. Trotzdem verlassen?')) {
    return;
  }
  router.push('/admin');
}

// Init
loadData();
</script>

<template>
  <div class="form-container">
    <div class="page-header">
      <h3 class="form-body-title">Kurs-Inhalte verwalten</h3>
      <div class="header-actions">
        <button class="buttonstyle" @click="goBack">← Zurück</button>
        <button class="buttonstyle save-btn" :disabled="saving || !isDirty" @click="saveAll">
          {{ saving ? 'Speichern...' : 'Speichern' }}
        </button>
      </div>
    </div>

    <div v-if="statusMessage" :class="['status-message', statusType]">
      {{ statusMessage }}
    </div>

    <div v-if="loading" class="loading">Laden...</div>

    <template v-else>
      <!-- Draggable groups -->
      <draggable
        v-model="groups"
        item-key="name"
        handle=".group-drag-handle"
        @change="onGroupDragChange"
      >
        <template #item="{ element: group, index: groupIndex }">
          <div class="group-card cardshadow">
            <div class="group-header">
              <span class="group-drag-handle" title="Gruppe verschieben">⠿</span>

              <template v-if="group.editing">
                <input
                  v-model="group.editName"
                  class="group-name-input"
                  @keyup.enter="confirmRenameGroup(group)"
                  @keyup.escape="cancelRenameGroup(group)"
                />
                <button class="btn-icon" @click="confirmRenameGroup(group)" title="Bestätigen">✓</button>
                <button class="btn-icon" @click="cancelRenameGroup(group)" title="Abbrechen">✕</button>
              </template>
              <template v-else>
                <h4 class="group-title" @click="group.collapsed = !group.collapsed">
                  {{ group.name }}
                  <span class="collapse-indicator">{{ group.collapsed ? '▸' : '▾' }}</span>
                  <span class="item-count">({{ group.items.length }})</span>
                </h4>
              </template>

              <div class="group-actions">
                <button type="button" class="buttonstyle" style="height:3rem" @click="startRenameGroup(group)">
                  <span>Umbenennen</span>
                </button>
                <button type="button" class="buttonstyle" style="height:3rem;margin-left:1rem" @click="deleteGroup(group, groupIndex)">
                  <span>Löschen</span>
                </button>
              </div>
            </div>

            <div v-show="!group.collapsed" class="group-body">
              <!-- Draggable items within group -->
              <draggable
                v-model="group.items"
                item-key="Title"
                group="courseItems"
                handle=".item-drag-handle"
                @change="onItemDragChange"
              >
                <template #item="{ element: item, index: itemIndex }">
                  <div class="item-row">
                    <span class="item-drag-handle" title="Verschieben">⠿</span>
                    <span class="item-type-icon">{{ getContentTypeIcon(item.ContentType) }}</span>
                    <span class="item-title">{{ item.Title || '(Ohne Titel)' }}</span>
                    <span class="item-rights-tags">
                      <span v-for="right in (item.GroupRights || [])" :key="right" class="rights-tag">{{ right }}</span>
                    </span>
                    <div class="item-actions">
                      <button type="button" class="buttonstyle" style="height:3rem" @click="openEditItem(item, groupIndex)">
                        <span>Bearbeiten</span>
                      </button>
                      <button type="button" class="buttonstyle" style="height:3rem;margin-left:1rem" @click="deleteItem(group, itemIndex)">
                        <span>Löschen</span>
                      </button>
                    </div>
                  </div>
                </template>
              </draggable>

              <button class="buttonstyle add-item-btn" @click="addItem(group)">+ Eintrag hinzufügen</button>
            </div>
          </div>
        </template>
      </draggable>

      <button class="buttonstyle add-group-btn" @click="addGroup">+ Neue Gruppe</button>
    </template>

    <!-- Edit Overlay -->
    <div v-if="showEditOverlay" class="overlay-backdrop" @click.self="cancelEdit">
      <div class="overlay-box">
        <h4>Eintrag bearbeiten</h4>

        <div class="form-field">
          <label class="label-style">Titel</label>
          <input v-model="editingItem!.Title" type="text" class="textbox-style" placeholder="z.B. Video 1: Natürliches Lernen" />
        </div>

        <div class="form-field">
          <label class="label-style">Inhaltstyp</label>
          <select v-model="editingItem!.ContentType" class="textbox-style">
            <option value="mp4">Video (mp4)</option>
            <option value="pdf">PDF</option>
          </select>
        </div>

        <div class="form-field">
          <label class="label-style">Content URL</label>
          <div class="url-input-row">
            <input v-model="editingItem!.ContentUrl" type="text" class="textbox-style" placeholder="URL eingeben" />
            <button v-if="editingItem!.ContentType === 'mp4'" type="button" class="buttonstyle upload-btn" @click="triggerFileUpload" :disabled="uploading">
              {{ uploading ? 'Lädt...' : 'Datei hochladen' }}
            </button>
          </div>
          <input
            ref="fileInputRef"
            type="file"
            accept="video/mp4,.mp4"
            style="display:none"
            @change="handleFileSelected"
          />
          <div v-if="uploading || uploadProgress === 100" class="progress-container">
            <div class="progress-bar" :style="{ width: uploadProgress + '%' }" :class="{ 'progress-complete': uploadProgress === 100 && !uploading }"></div>
            <span class="progress-text">{{ uploadProgress }}%</span>
          </div>
          <div v-if="uploadError" class="upload-error">{{ uploadError }}</div>
        </div>

        <div class="form-field">
          <label class="label-style">Berechtigungen (GroupRights)</label>
          <div class="rights-checkboxes">
            <label v-for="right in availableGroupRights" :key="right" class="rights-checkbox-label">
              <input
                type="checkbox"
                :checked="editingItem!.GroupRights?.includes(right)"
                @change="toggleGroupRight(right)"
              />
              {{ right }}
            </label>
          </div>
        </div>

        <div class="overlay-actions">
          <button class="buttonstyle" @click="saveEditItem">Übernehmen</button>
          <button class="buttonstyle cancel-btn" @click="cancelEdit">Abbrechen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.save-btn:not(:disabled) {
  background-color: var(--main-color);
  color: white;
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.status-message {
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-weight: bold;
}
.status-message.success {
  background-color: #dcedc8;
  color: #33691e;
}
.status-message.error {
  background-color: #ffcdd2;
  color: #b71c1c;
}

.loading {
  text-align: center;
  padding: 4rem;
  color: #888;
  font-size: 1.4rem;
}

/* Group card */
.group-card {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: 1px solid #e0e0e0;
}

.group-header {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: var(--secondary-color, #f5f5f5);
  border-bottom: 1px solid #e0e0e0;
  gap: 0.75rem;
}

.group-drag-handle {
  cursor: grab;
  font-size: 1.4rem;
  color: #999;
  user-select: none;
}

.group-drag-handle:active {
  cursor: grabbing;
}

.group-title {
  flex: 1;
  margin: 0;
  cursor: pointer;
  user-select: none;
}

.collapse-indicator {
  font-size: 0.9em;
  margin-left: 0.5rem;
  color: #888;
}

.item-count {
  font-weight: normal;
  font-size: 0.85em;
  color: #888;
  margin-left: 0.5rem;
}

.group-name-input {
  flex: 1;
  padding: 0.4rem 0.8rem;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.group-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

.group-body {
  padding: 1rem 1.5rem;
}

/* Item row */
.item-row {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #f0f0f0;
  gap: 0.75rem;
}

.item-row:last-child {
  border-bottom: none;
}

.item-drag-handle {
  cursor: grab;
  color: #bbb;
  font-size: 1.6rem;
  user-select: none;
}

.item-drag-handle:active {
  cursor: grabbing;
}

.item-type-icon {
  font-size: 1.6rem;
  min-width: 2rem;
  text-align: center;
}

.item-title {
  flex: 1;
  font-size: 1.6rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-rights-tags {
  display: flex;
  gap: 0.3rem;
  flex-wrap: nowrap;
}

.rights-tag {
  background: #e8f5e9;
  color: #2e7d32;
  padding: 0.2rem 0.6rem;
  border-radius: 3px;
  font-size: 1.2rem;
  white-space: nowrap;
}

.item-actions {
  display: flex;
  gap: 0;
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
}

.btn-icon:hover {
  background-color: #f0f0f0;
}

.add-item-btn {
  margin-top: 1rem;
}

.add-group-btn {
  margin-top: 1rem;
  margin-bottom: 2rem;
}

/* Overlay */
.overlay-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.overlay-box {
  background: #fff;
  border-radius: 8px;
  padding: 2rem;
  width: 600px;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}

.overlay-box h4 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 2rem;
}

.form-field {
  margin-bottom: 1.25rem;
}

.url-input-row {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.url-input-row .textbox-style {
  flex: 1;
}

.upload-btn {
  white-space: nowrap;
  height: 50px;
}

.progress-container {
  margin-top: 0.8rem;
  background: #e0e0e0;
  border-radius: 5px;
  height: 2.5rem;
  position: relative;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: var(--main-color, #8BC34A);
  border-radius: 5px;
  transition: width 0.2s ease;
}

.progress-bar.progress-complete {
  background-color: #4CAF50;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.2rem;
  font-weight: bold;
  color: #333;
}

.upload-error {
  margin-top: 0.5rem;
  color: #b71c1c;
  font-size: 1.4rem;
}

.rights-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.rights-checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.6rem;
  cursor: pointer;
  padding: 0.4rem 0.8rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
}

.rights-checkbox-label:hover {
  background: #f0f0f0;
}

.overlay-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cancel-btn {
  background-color: #eee;
}

@media only screen and (max-width: 629px) {
  .item-rights-tags {
    display: none;
  }

  .item-title {
    max-width: 150px;
  }
}
</style>
